import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { isCancel } from "axios";
import { useParams } from "react-router-dom";
import type { Promisable } from "type-fest";

import {
  userOverridableConfigurationKeys,
  type UserOrganisationConfiguration,
  type UserOrganisationConfigurationSummary,
  type UserOverridableConfigurationKey,
  type UUID,
} from "@thalamos/aspire-common";

import { api, apiHooks } from "../../api.js";
import { routeFns } from "../../routePaths.js";
import {
  renderErrorToast,
  renderSuccessToast,
} from "~/components/design-system/index.js";

const KEY_LABELS: Record<UserOverridableConfigurationKey, string> = {
  patientListEnabled: "Patient list",
};

type Choice = "inherit" | "on" | "off";

const CHOICE_LABELS: Record<Choice, string> = {
  inherit: "Inherit",
  on: "On",
  off: "Off",
};

const CHOICE_VALUES: Record<Choice, boolean | undefined> = {
  inherit: undefined,
  on: true,
  off: false,
};

function choiceFor(value: boolean | undefined): Choice {
  if (value === undefined) {
    return "inherit";
  }
  return value ? "on" : "off";
}

const onOff = (value: boolean) => (value ? "On" : "Off");

export interface UserOrganisationConfigurationPageInnerProps {
  userName: string;
  userEmail: string;
  profileHref: string;
  summaries: UserOrganisationConfigurationSummary[];
  /**
   * While a change is being saved and the settings reloaded, no other can be
   * made.
   */
  isSaving: boolean;
  onChange: (
    organisationId: UUID,
    overrides: UserOrganisationConfiguration,
  ) => Promisable<void>;
}

export function UserOrganisationConfigurationPageInner({
  userName,
  userEmail,
  profileHref,
  summaries,
  isSaving,
  onChange,
}: UserOrganisationConfigurationPageInnerProps) {
  return (
    <>
      <h3>
        Organisation settings: {userName} - {userEmail} (
        <a href={profileHref}>profile</a>)
      </h3>
      <Typography sx={{ mb: 2 }}>
        Set a feature for this user alone. A setting applies in the organisation
        it is set in and in every organisation below it, whatever those
        organisations set, unless this user has a setting of their own lower
        down. Inherit takes the setting this user has on an organisation above,
        or else the setting of the organisation itself.
      </Typography>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Organisation</TableCell>
            <TableCell>Teams</TableCell>
            {userOverridableConfigurationKeys.map((key) => (
              <TableCell key={key}>{KEY_LABELS[key]}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {summaries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={2 + userOverridableConfigurationKeys.length}>
                This user works in no organisations
              </TableCell>
            </TableRow>
          ) : (
            summaries.map((summary) => (
              <TableRow key={summary.organisationId}>
                <TableCell>
                  {summary.organisationName}
                  {summary.isMember ? null : (
                    <Typography variant="body2" color="text.secondary">
                      No longer a member
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{summary.teamNames.join(", ") || "-"}</TableCell>
                {userOverridableConfigurationKeys.map((key) => (
                  <TableCell key={key}>
                    <ToggleButtonGroup
                      exclusive
                      disabled={isSaving}
                      size="small"
                      color="primary"
                      aria-label={`${KEY_LABELS[key]} in ${summary.organisationName}`}
                      value={choiceFor(summary.overrides[key])}
                      onChange={(_, choice: Choice | null) => {
                        if (choice === null) {
                          return;
                        }
                        void onChange(summary.organisationId, {
                          ...summary.overrides,
                          [key]: CHOICE_VALUES[choice],
                        });
                      }}
                    >
                      {(["inherit", "on", "off"] as const).map((choice) => (
                        <ToggleButton
                          key={choice}
                          value={choice}
                          sx={{ textTransform: "none" }}
                        >
                          {CHOICE_LABELS[choice]}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                    <Typography variant="body2" color="text.secondary">
                      Organisation: {onOff(summary.organisationValues[key])} ·
                      This user: {onOff(summary.effectiveValues[key])}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </>
  );
}

function CannotBeShown() {
  return (
    <Typography role="alert" color="error">
      This user cannot be shown.
    </Typography>
  );
}

export function UserOrganisationConfigurationPage() {
  const { userId } = useParams();

  if (!userId) {
    return <CannotBeShown />;
  }

  return <UserOrganisationConfigurationLoader userId={userId} />;
}

function UserOrganisationConfigurationLoader({ userId }: { userId: UUID }) {
  const [isSaving, setIsSaving] = useState(false);

  const [{ data: user, loading: userLoading }] =
    apiHooks.users.getUserViaPusher(userId);

  const [{ data: summaries, loading: summariesLoading }, refetchSummaries] =
    apiHooks.users.getOrganisationConfiguration(userId);

  if (userLoading || (summariesLoading && !summaries)) {
    return <div />;
  }

  if (!user || !summaries) {
    return <CannotBeShown />;
  }

  const reloadSummaries = async () => {
    try {
      await refetchSummaries();
    } catch (error: unknown) {
      // A reload is cancelled when another request replaces it or the page
      // unmounts; either way its outcome no longer matters.
      if (!isCancel(error)) {
        renderErrorToast({ message: "Failed to reload the user's settings" });
      }
    }
  };

  const onChange = async (
    organisationId: UUID,
    overrides: UserOrganisationConfiguration,
  ) => {
    setIsSaving(true);
    try {
      const result = await api.users.setOrganisationConfiguration(
        user.id,
        organisationId,
        overrides,
      );
      if (result.status === 204) {
        renderSuccessToast({ message: "Saved the user's setting" });
      } else {
        renderErrorToast({ message: "Failed to save the user's setting" });
      }
    } catch {
      renderErrorToast({ message: "Failed to save the user's setting" });
    } finally {
      // Hold the choices disabled until the reload settles: each change sends
      // the organisation's whole overrides as shown, which may be out of date
      // until then. reloadSummaries never throws, so they are always re-enabled.
      await reloadSummaries();
      setIsSaving(false);
    }
  };

  return (
    <UserOrganisationConfigurationPageInner
      userName={user.name}
      userEmail={user.email}
      profileHref={routeFns.userProfilePage(user.id)}
      summaries={summaries}
      isSaving={isSaving}
      onChange={onChange}
    />
  );
}
